import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Upload, Loader2, MapPinned, Plus, Trash2, Pencil, RefreshCcw, Users, Megaphone, Share2, ExternalLink, CalendarClock, ShieldCheck, Save, Flag } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api, uploadFile } from '../../lib/api';
import { toast } from 'sonner';
import { AdminCrud } from './AdminCrud';

const ELECTION_CATEGORIES = [
  { value: 'official_ckl_update', label: 'Official CKL Update' },
  { value: 'voting_process', label: 'Voting Process' },
  { value: 'candidate_update', label: 'Candidate Update' },
  { value: 'debate_forum', label: 'Debate / Forum' },
  { value: 'ward_boundary', label: 'Ward Boundary' },
  { value: 'council_meeting', label: 'Council Meeting' },
  { value: 'local_news', label: 'Local News' },
  { value: 'school_board_trustee', label: 'School Board Trustee' },
  { value: 'steves_statement', label: "Steve's Statement" },
  { value: 'other', label: 'Other' },
];

const ITEM_STATUSES = ['draft', 'needs_review', 'verified', 'published', 'hidden', 'archived'];
const DEBATE_STATUSES = ['rumoured', 'needs_review', 'confirmed', 'completed', 'cancelled'];

// ------------------------------------------------ generic list-of-rows editor ----
const RowsEditor = ({ label, rows = [], onChange, fields }) => (
  <div className="space-y-2">
    <p className="text-sm font-bold text-[#1a2744]">{label}</p>
    {rows.map((row, i) => (
      <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-2">
        {fields.map((f) => (
          <Input
            key={f.key}
            value={row[f.key] || ''}
            placeholder={f.placeholder}
            onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [f.key]: e.target.value } : r)))}
            className="h-10 text-sm flex-1 min-w-[140px]"
          />
        ))}
        <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 shrink-0" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button variant="outline" size="sm" className="h-9 font-bold" onClick={() => onChange([...rows, {}])}><Plus className="h-4 w-4 mr-1" /> Add row</Button>
  </div>
);

// ------------------------------------------------------------ Content tab ----
const ContentTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => setForm(r.data?.election || {})).catch(() => setForm({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { key: 'election', value: form });
      toast.success('Election Centre content saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-slate-500">Loading…</p>;
  const set = (k, v) => setForm({ ...form, [k]: v });
  const hero = form.hero || {};
  const setHero = (k, v) => setForm({ ...form, hero: { ...hero, [k]: v } });

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Phase 79: Election Centre hero editor */}
      <Card className="border-slate-200" data-testid="election-hero-editor">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-serif font-bold text-[#1a2744]">Election Centre hero</p>
              <p className="text-xs text-slate-500 mt-0.5">Shown at the top of /elections. Leave a field blank to use the standard wording. Button and card links that start with http open in a new tab.</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-bold text-slate-600">Show hero</span>
              <Switch checked={hero.enabled !== false} onCheckedChange={(v) => setHero('enabled', v)} data-testid="election-hero-enabled" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-sm font-bold text-[#1a2744] mb-1">Eyebrow</p><Input value={hero.eyebrow ?? ''} placeholder="2026 Municipal Election" onChange={(e) => setHero('eyebrow', e.target.value)} className="h-11" data-testid="election-hero-eyebrow-input" /></div>
            <div><p className="text-sm font-bold text-[#1a2744] mb-1">Headline</p><Input value={hero.headline ?? ''} placeholder="Kawartha Lakes 2026 Election Centre" onChange={(e) => setHero('headline', e.target.value)} className="h-11" data-testid="election-hero-headline-input" /></div>
          </div>
          <div><p className="text-sm font-bold text-[#1a2744] mb-1">Subheadline</p><Textarea value={hero.subheadline ?? ''} onChange={(e) => setHero('subheadline', e.target.value)} rows={2} data-testid="election-hero-subheadline-input" /></div>
          <div><p className="text-sm font-bold text-[#1a2744] mb-1">Body copy</p><Textarea value={hero.body ?? ''} onChange={(e) => setHero('body', e.target.value)} rows={3} data-testid="election-hero-body-input" /></div>
          <div><p className="text-sm font-bold text-[#1a2744] mb-1">Note (confirm-with-the-City reminder)</p><Textarea value={hero.note ?? ''} onChange={(e) => setHero('note', e.target.value)} rows={2} data-testid="election-hero-note-input" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-sm font-bold text-[#1a2744] mb-1">Source note (optional, small print)</p><Input value={hero.source_note ?? ''} onChange={(e) => setHero('source_note', e.target.value)} className="h-11" data-testid="election-hero-source-note-input" /></div>
            <div><p className="text-sm font-bold text-[#1a2744] mb-1">Background image URL (optional)</p><Input value={hero.background_image ?? ''} placeholder="/api/uploads/... (approved image only)" onChange={(e) => setHero('background_image', e.target.value)} className="h-11" data-testid="election-hero-bg-input" /></div>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">Only use approved civic images. No restricted CKL imagery, no Google Maps screenshots, no placeholders. Leave blank for the standard navy background.</p>
          <RowsEditor label="Hero buttons (first button is gold)" rows={hero.buttons || []} onChange={(v) => setHero('buttons', v)} fields={[{ key: 'label', placeholder: 'Button label' }, { key: 'url', placeholder: 'Link (/whos-running or https://...)' }]} />
          <RowsEditor label="Quick link cards (below the buttons)" rows={hero.cards || []} onChange={(v) => setHero('cards', v)} fields={[{ key: 'label', placeholder: 'Card label' }, { key: 'url', placeholder: 'Link' }]} />
          <p className="text-xs text-slate-400">Leave buttons or cards empty to use the standard set (Find Your Ward, Who's Running, Key Election Dates, Official CKL Election Resources).</p>
        </CardContent>
      </Card>

      <div><p className="text-sm font-bold text-[#1a2744] mb-1">Page title</p><Input value={form.title || ''} onChange={(e) => set('title', e.target.value)} className="h-11" data-testid="election-admin-title" /></div>
      <div><p className="text-sm font-bold text-[#1a2744] mb-1">Intro (shown under the title)</p><Textarea value={form.intro || ''} onChange={(e) => set('intro', e.target.value)} rows={3} data-testid="election-admin-intro" /></div>
      <div><p className="text-sm font-bold text-[#1a2744] mb-1">About the 2026 Municipal Election</p><Textarea value={form.about || ''} onChange={(e) => set('about', e.target.value)} rows={3} /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><p className="text-sm font-bold text-[#1a2744] mb-1">Election date</p><Input type="date" value={form.election_date || ''} onChange={(e) => set('election_date', e.target.value)} className="h-11" /></div>
        <div><p className="text-sm font-bold text-[#1a2744] mb-1">Official CKL elections URL</p><Input value={form.official_url || ''} onChange={(e) => set('official_url', e.target.value)} className="h-11" /></div>
      </div>
      <div><p className="text-sm font-bold text-[#1a2744] mb-1">Ward 8 boundary summary</p><Textarea value={form.ward_boundary_summary || ''} onChange={(e) => set('ward_boundary_summary', e.target.value)} rows={2} /></div>
      <RowsEditor label="Voter resource cards" rows={form.voter_resources || []} onChange={(v) => set('voter_resources', v)} fields={[{ key: 'title', placeholder: 'Title' }, { key: 'url', placeholder: 'URL' }, { key: 'note', placeholder: 'Label (e.g. Official CKL Source)' }]} />
      <RowsEditor label="Candidate resource cards" rows={form.candidate_resources || []} onChange={(v) => set('candidate_resources', v)} fields={[{ key: 'title', placeholder: 'Title' }, { key: 'url', placeholder: 'URL' }, { key: 'note', placeholder: 'Label' }]} />
      <RowsEditor label="School Board Trustee cards" rows={form.school_board || []} onChange={(v) => set('school_board', v)} fields={[{ key: 'title', placeholder: 'Title' }, { key: 'url', placeholder: 'URL' }, { key: 'note', placeholder: 'Label' }]} />
      <RowsEditor label="Official links" rows={form.official_links || []} onChange={(v) => set('official_links', v)} fields={[{ key: 'title', placeholder: 'Title' }, { key: 'url', placeholder: 'URL' }]} />
      <RowsEditor label="Key dates" rows={form.key_dates || []} onChange={(v) => set('key_dates', v)} fields={[{ key: 'date', placeholder: 'YYYY-MM-DD' }, { key: 'label', placeholder: 'Label' }, { key: 'note', placeholder: 'Note / source label' }]} />
      <RowsEditor label="Voting FAQ" rows={form.faq || []} onChange={(v) => set('faq', v)} fields={[{ key: 'q', placeholder: 'Question' }, { key: 'a', placeholder: 'Answer (plain language)' }, { key: 'source_url', placeholder: 'Source URL (optional)' }]} />
      <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="election-admin-save">
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Election Centre content
      </Button>
    </div>
  );
};

// ------------------------------------------------------ Key Dates tab ----
// Phase 57: easy add/edit of key countdown dates. Admin-only — the countdown
// is never published publicly unless Steve later chooses to publish it.
const KD_FIELDS = [
  ['campaign_launch', 'Campaign launch date', 'When the campaign publicly launches.'],
  ['advance_voting_start', 'Advance voting start date', 'First day of advance voting.'],
  ['final_voting_day', 'Final voting day', 'Leave blank to use the Election Centre election date automatically.'],
  ['nomination_open', 'Nomination / filing opens', 'Optional — when candidate filing opens.'],
  ['nomination_close', 'Nomination / filing closes', 'Optional — candidate filing deadline.'],
];

const KeyDatesTab = () => {
  const [cd, setCd] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/election-countdown').then((r) => {
      setCd(r.data);
      setError(false);
      const kd = r.data.key_dates || {};
      setForm({
        campaign_launch: kd.campaign_launch || '',
        advance_voting_start: kd.advance_voting_start || '',
        final_voting_day: kd.final_voting_day || '',
        nomination_open: kd.nomination_open || '',
        nomination_close: kd.nomination_close || '',
        custom: (kd.custom || []).map((c) => ({ ...c, show: c.show !== false })),
      });
    }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/election-countdown/dates', form);
      toast.success('Key dates saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Saving key dates failed'); }
    setSaving(false);
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="key-dates-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load key dates.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!cd || !form) return <p className="text-sm text-slate-500">Loading…</p>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (rows) => setForm((f) => ({ ...f, custom: rows }));

  return (
    <div className="space-y-5 max-w-3xl" data-testid="key-dates-tab">
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start" data-testid="key-dates-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">These dates power the private admin Election Countdown on the dashboard. The countdown is <strong>never published publicly</strong> unless Steve later chooses to publish it.</p>
      </div>

      {cd.needs_setup && (
        <div className="rounded-xl border border-dashed border-[#C9A84C]/60 bg-[#C9A84C]/5 p-3" data-testid="key-dates-setup-prompt">
          <p className="text-sm font-bold text-[#7A5C12]">{cd.setup_prompt}</p>
          <p className="text-xs text-slate-500 mt-0.5">Add your campaign launch and advance voting dates below{cd.election_centre_date ? ` — the final voting day (${cd.election_centre_date}) is already pulled from the Election Centre.` : '.'}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {KD_FIELDS.map(([key, label, help]) => (
          <div key={key}>
            <p className="text-sm font-bold text-[#1a2744] mb-1">{label}</p>
            <Input type="date" value={form[key]} onChange={(e) => set(key, e.target.value)} className="h-11" data-testid={`key-date-input-${key.replace(/_/g, '-')}`} />
            <p className="text-[11px] text-slate-500 mt-1">{help}{key === 'final_voting_day' && cd.election_centre_date ? ` Election Centre date: ${cd.election_centre_date}.` : ''}</p>
          </div>
        ))}
      </div>

      <div className="space-y-2" data-testid="key-dates-custom-section">
        <p className="text-sm font-bold text-[#1a2744]">Custom key dates</p>
        <p className="text-xs text-slate-500">e.g. sign deadline, all-candidates meeting, canvass kickoff. Use the switch to show or hide each one in the admin countdown.</p>
        {form.custom.map((row, i) => (
          <div key={row.id || i} className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`custom-key-date-row-${i}`}>
            <div className="flex flex-wrap gap-2 items-start">
              <Input value={row.title || ''} placeholder="Title (e.g. Sign deadline)" onChange={(e) => setCustom(form.custom.map((r, j) => (j === i ? { ...r, title: e.target.value } : r)))} className="h-10 text-sm flex-1 min-w-[160px]" data-testid={`custom-key-date-title-${i}`} />
              <Input type="date" value={row.date || ''} onChange={(e) => setCustom(form.custom.map((r, j) => (j === i ? { ...r, date: e.target.value } : r)))} className="h-10 text-sm w-40" data-testid={`custom-key-date-date-${i}`} />
              <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 h-10 shrink-0">
                <Switch checked={row.show !== false} onCheckedChange={(v) => setCustom(form.custom.map((r, j) => (j === i ? { ...r, show: v } : r)))} data-testid={`custom-key-date-show-${i}`} />
                {row.show !== false ? 'Shown' : 'Hidden'}
              </label>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 shrink-0" onClick={() => setCustom(form.custom.filter((_, j) => j !== i))} data-testid={`custom-key-date-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <Input value={row.description || ''} placeholder="Description (optional)" onChange={(e) => setCustom(form.custom.map((r, j) => (j === i ? { ...r, description: e.target.value } : r)))} className="h-10 text-sm" data-testid={`custom-key-date-desc-${i}`} />
          </div>
        ))}
        <Button variant="outline" size="sm" className="h-9 font-bold" onClick={() => setCustom([...form.custom, { title: '', date: '', description: '', show: true }])} data-testid="custom-key-date-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add custom key date
        </Button>
      </div>

      <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="key-dates-save-btn">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Key Dates
      </Button>

      {cd.entries.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="key-dates-preview">
          <p className="text-xs font-bold uppercase tracking-wide text-[#7A5C12] flex items-center gap-1.5 mb-2"><CalendarClock className="h-3.5 w-3.5" /> Countdown preview (admin-only)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {cd.entries.filter((e) => !e.past).slice(0, 8).map((e) => (
              <div key={`${e.label}-${e.date}`} className="rounded-lg border border-slate-200 p-2.5 bg-white">
                <p className="font-serif font-black text-xl text-[#1a2744]">{e.days_until}<span className="text-xs font-sans font-bold text-slate-400 ml-1">days</span></p>
                <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 leading-tight">{e.label}</p>
                <p className="text-[10px] text-slate-400">{e.date}</p>
                {e.description && <p className="text-[10px] text-slate-500 mt-0.5">{e.description}</p>}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-2">{cd.note}</p>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------- Wards tab ----
// ------------------------------------------ Ward Finder (address lookup) ----
const WF_TEXT_FIELDS = [
  ['title', 'Address search title'],
  ['placeholder', 'Placeholder text'],
  ['helper_text', 'Helper text'],
  ['privacy_note', 'Privacy note'],
  ['no_match_message', 'No-match message'],
  ['no_results_hint', 'No-results typing hint (generic example only)'],
  ['fallback_message', 'Public fallback message (provider unavailable)'],
  ['disclaimer', 'Disclaimer text'],
  ['source_name', 'Official source name'],
  ['source_url', 'Official source link'],
];

const WF_PROVIDERS = [
  { value: 'nominatim', label: 'OpenStreetMap / Nominatim (free, no key)' },
  { value: 'arcgis', label: 'ArcGIS World Geocoder (free for search; key optional)' },
  { value: 'google', label: 'Google Places / Geocoding (key required)' },
  { value: 'mapbox', label: 'Mapbox (token required)' },
];

/* Miss patterns: which communities, streets and reasons get flagged most. */
const BarList = ({ title, items, testId, empty = 'Nothing flagged yet.' }) => (
  <div data-testid={testId}>
    <p className="text-xs font-black uppercase tracking-wider text-slate-500">{title}</p>
    {(!items || items.length === 0) && <p className="text-xs text-slate-400 mt-1.5">{empty}</p>}
    <ul className="mt-1.5 space-y-1.5">
      {(items || []).map((it) => (
        <li key={it.label} className="text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-700 truncate">{it.label}</span>
            <span className="font-bold text-[#1a2744] shrink-0">{it.count}{it.share != null ? ` · ${it.share}%` : ''}</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-[#1a2744] rounded-full" style={{ width: `${Math.max(4, Math.min(100, it.share ?? 0))}%` }} /></div>
        </li>
      ))}
    </ul>
  </div>
);

const WardFinderMissPatterns = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/ward-finder/feedback/patterns', { params: { days } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load miss patterns');
    } finally { setBusy(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const maxWeek = Math.max(1, ...((data?.weekly || []).map((w) => w.count)));
  return (
    <Card data-testid="ward-finder-patterns-panel">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-serif font-bold text-[#1a2744]">Where the lookup needs work</p>
            <p className="text-xs text-slate-500 mt-1">Communities, streets and reasons that visitors flag most. Streets that keep appearing are the best ones to test and fix first.</p>
          </div>
          <div className="flex gap-1.5">
            {[[30, '30 days'], [90, '90 days'], [365, '12 months']].map(([d, l]) => (
              <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'} onClick={() => setDays(d)} className={`h-9 text-xs font-bold ${days === d ? 'bg-[#1a2744] text-white' : ''}`} data-testid={`ward-finder-patterns-days-${d}`}>{l}</Button>
            ))}
          </div>
        </div>
        {busy && !data && <p className="text-sm text-slate-500 mt-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>}
        {data && (
          <>
            <div className="flex flex-wrap gap-2 mt-4 text-xs" data-testid="ward-finder-patterns-summary">
              <span className="bg-slate-100 rounded-full px-2.5 py-1"><strong>{data.total}</strong> flags in {data.days} days</span>
              <span className={`rounded-full px-2.5 py-1 border ${data.open ? 'bg-amber-50 text-amber-900 border-amber-300' : 'bg-slate-100 border-slate-200'}`}><strong>{data.open}</strong> still new</span>
            </div>
            {data.total === 0 ? (
              <p className="text-sm text-slate-500 mt-4" data-testid="ward-finder-patterns-empty">No visitor flags in this period. When residents press "This isn't my location", the patterns show up here.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-5">
                  <BarList title="Communities" items={data.communities} testId="ward-finder-patterns-communities" />
                  <BarList title="Reasons" items={data.reasons} testId="ward-finder-patterns-reasons" />
                  <BarList title="What was typed" items={data.kinds} testId="ward-finder-patterns-kinds" />
                </div>
                <div className="mt-6" data-testid="ward-finder-patterns-streets">
                  <p className="text-xs font-black uppercase tracking-wider text-slate-500">Streets flagged most</p>
                  {data.streets.length === 0 ? <p className="text-xs text-slate-400 mt-1.5">No street-level flags yet.</p> : (
                    <table className="w-full text-xs mt-1.5">
                      <thead><tr className="text-left text-slate-500"><th className="py-1 pr-3">Street (community typed)</th><th className="py-1 pr-3">Flags</th><th className="py-1 pr-3">Still new</th><th className="py-1 pr-3">Most common reason</th><th className="py-1">Last</th></tr></thead>
                      <tbody>
                        {data.streets.map((st) => (
                          <tr key={st.label} className="border-t border-slate-100" data-testid="ward-finder-patterns-street-row">
                            <td className="py-1.5 pr-3 font-semibold text-[#1a2744]">{st.label}</td>
                            <td className="py-1.5 pr-3">{st.count}</td>
                            <td className="py-1.5 pr-3">{st.open}</td>
                            <td className="py-1.5 pr-3 text-slate-700">{st.top_reason}</td>
                            <td className="py-1.5 text-slate-500">{st.last}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                {data.intersections.length > 0 && <div className="mt-6"><BarList title="Intersections flagged" items={data.intersections} testId="ward-finder-patterns-intersections" /></div>}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <BarList title="Match type shown when flagged" items={data.match_types} testId="ward-finder-patterns-match-types" />
                  <BarList title="Ward shown when flagged" items={data.wards_shown} testId="ward-finder-patterns-wards" empty="No ward was shown on flagged lookups." />
                </div>
                {data.weekly.length > 0 && (
                  <div className="mt-6" data-testid="ward-finder-patterns-weekly">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-500">Flags per week</p>
                    <div className="flex items-end gap-1.5 h-20 mt-2">
                      {data.weekly.map((w) => (
                        <div key={w.week} className="w-12 flex flex-col items-center justify-end gap-1" title={`${w.week}: ${w.count}`} data-testid="ward-finder-patterns-week">
                          <span className="text-[10px] font-bold text-[#1a2744]">{w.count}</span>
                          <div className="w-full bg-[#1a2744] rounded-t" style={{ height: `${Math.max(6, (w.count / maxWeek) * 48)}px` }} />
                          <span className="text-[9px] text-slate-500">{w.week.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            <p className="text-[11px] text-slate-500 mt-4">{data.note}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/* Visitor-flagged address lookup misses ("This isn't my location"). Entries are
   stored with house numbers and postal codes already removed. */
const WardFinderFeedback = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('new');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/ward-finder/feedback', { params: filter === 'all' ? {} : { status: filter } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load lookup feedback');
    } finally { setBusy(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/ward-finder/feedback/${id}/status`, { status }); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/ward-finder/feedback/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const counts = data?.counts || {};
  return (
    <Card data-testid="ward-finder-feedback-panel">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Flag className="h-4 w-4 text-[#b91c1c]" /> Address lookup misses flagged by visitors</p>
            <p className="text-xs text-slate-500 mt-1">Visitors pressed "This isn't my location". House numbers and postal codes were removed before saving; nothing identifies a home.</p>
          </div>
          <div className="flex gap-1.5">
            {[['new', 'New'], ['reviewed', 'Reviewed'], ['fixed', 'Fixed'], ['all', 'All']].map(([k, l]) => (
              <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)} className={`h-9 text-xs font-bold ${filter === k ? 'bg-[#1a2744] text-white' : ''}`} data-testid={`ward-finder-feedback-filter-${k}`}>
                {l}{k !== 'all' && counts[k] != null ? ` (${counts[k]})` : ''}
              </Button>
            ))}
          </div>
        </div>
        {busy && !data && <p className="text-sm text-slate-500 mt-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>}
        {data && data.rows.length === 0 && <p className="text-sm text-slate-500 mt-4" data-testid="ward-finder-feedback-empty">No flagged lookups{filter !== 'all' ? ` with status "${filter}"` : ''}.</p>}
        {data && data.rows.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs" data-testid="ward-finder-feedback-table">
              <thead><tr className="text-left text-slate-500"><th className="py-1.5 pr-3">When</th><th className="py-1.5 pr-3">Reason</th><th className="py-1.5 pr-3">Typed (redacted)</th><th className="py-1.5 pr-3">Match shown</th><th className="py-1.5 pr-3">Note</th><th className="py-1.5">Actions</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`ward-finder-feedback-row-${r.id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{(r.created_at || '').slice(0, 10)}<div className="text-[10px] text-slate-400 capitalize">{r.status}</div></td>
                    <td className="py-2 pr-3 font-semibold text-[#1a2744]">{r.reason_label}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.query_redacted || <span className="text-slate-400">none</span>}<div className="text-[10px] text-slate-500">{r.parsed_kind}{r.community_typed ? ` · ${r.community_typed}` : ''}{r.street_typed ? ` · ${r.street_typed}` : ''}</div></td>
                    <td className="py-2 pr-3 text-slate-700">{r.chosen_label_redacted || <span className="text-slate-400">no match chosen</span>}<div className="text-[10px] text-slate-500">{r.match_type}{r.ward_shown ? ` · Ward ${r.ward_shown}` : ''}</div></td>
                    <td className="py-2 pr-3 text-slate-600 max-w-[200px]">{r.note_redacted || '-'}</td>
                    <td className="py-2 whitespace-nowrap">
                      {r.status !== 'reviewed' && <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'reviewed')} className="h-8 text-[11px] font-bold mr-1" data-testid={`ward-finder-feedback-reviewed-${r.id}`}>Reviewed</Button>}
                      {r.status !== 'fixed' && <Button size="sm" variant="outline" onClick={() => setStatus(r.id, 'fixed')} className="h-8 text-[11px] font-bold mr-1" data-testid={`ward-finder-feedback-fixed-${r.id}`}>Fixed</Button>}
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} className="h-8 text-[11px] text-red-700" aria-label="Delete" data-testid={`ward-finder-feedback-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const WardFinderSettings = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/admin/settings'),
      api.get('/ward-finder/config'),
    ]).then(([adminS, pub]) => {
      // merge public defaults with any saved admin values (admin values win)
      setF({ ...(pub.data || {}), api_key: '', country: 'ca', province: 'Ontario', region_bias: 'City of Kawartha Lakes', ...(adminS.data?.ward_finder || {}) });
    }).catch(() => toast.error('Failed to load Ward Finder settings'));
  }, []);

  if (!f) return null;
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const value = { ...f };
      delete value.provider_ready;
      await api.put('/settings', { key: 'ward_finder', value });
      toast.success('Ward Finder settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="ward-finder-settings">
      <CardContent className="p-4 sm:p-5">
        <p className="font-serif font-bold text-[#1a2744]">Ward Finder — address lookup settings</p>
        <p className="text-xs text-slate-600 mt-1 mb-1.5 max-w-2xl">
          Powers the "Find Your Ward by Address" search on the public Who's Running page. Searched addresses are never stored, never logged, and never sent to AI. The API key is never exposed publicly.
        </p>
        <p className="text-xs font-semibold text-sky-900 bg-sky-50 border border-sky-200 rounded-lg p-2.5 mb-3 max-w-2xl" data-testid="ward-finder-provider-note">
          OpenStreetMap/Nominatim is the starter provider. For higher-volume public use, consider switching to Google Places, Mapbox, ArcGIS, or another configured provider.
        </p>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Enable address lookup
              <Switch checked={f.enabled !== false} onCheckedChange={(v) => set('enabled', v)} data-testid="wf-setting-enabled" />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><p className="text-xs font-bold text-slate-600 mb-1">Primary provider</p>
              <Select value={f.provider || 'nominatim'} onValueChange={(v) => set('provider', v)}>
                <SelectTrigger className="h-11" data-testid="wf-setting-provider"><SelectValue /></SelectTrigger>
                <SelectContent>{WF_PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Fallback provider (covers data gaps, e.g. Omemee streets)</p>
              <Select value={f.fallback_provider || 'arcgis'} onValueChange={(v) => set('fallback_provider', v)}>
                <SelectTrigger className="h-11" data-testid="wf-setting-fallback-provider"><SelectValue /></SelectTrigger>
                <SelectContent>{WF_PROVIDERS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">API key / token (stored privately; not needed for Nominatim/ArcGIS)</p>
              <Input type="password" value={f.api_key || ''} onChange={(e) => set('api_key', e.target.value)} placeholder="Provider API key (never shown publicly)" className="h-11" autoComplete="new-password" data-testid="wf-setting-api-key" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Enable fallback provider
              <Switch checked={f.enable_fallback !== false} onCheckedChange={(v) => set('enable_fallback', v)} data-testid="wf-setting-enable-fallback" />
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Show approximate street-level matches
              <Switch checked={f.show_approximate !== false} onCheckedChange={(v) => set('show_approximate', v)} data-testid="wf-setting-show-approximate" />
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Min confidence (0-100)
              <Input type="number" min="0" max="100" value={f.min_confidence ?? 80} onChange={(e) => set('min_confidence', parseInt(e.target.value || 80, 10))} className="h-10 w-24" data-testid="wf-setting-min-confidence" />
            </label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div><p className="text-xs font-bold text-slate-600 mb-1">Country</p><Input value={f.country || 'ca'} onChange={(e) => set('country', e.target.value)} className="h-11" /></div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Province</p><Input value={f.province || 'Ontario'} onChange={(e) => set('province', e.target.value)} className="h-11" /></div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">City/region bias</p><Input value={f.region_bias || 'City of Kawartha Lakes'} onChange={(e) => set('region_bias', e.target.value)} className="h-11" /></div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Result limit (1-8)</p><Input type="number" min="1" max="8" value={f.result_limit ?? 5} onChange={(e) => set('result_limit', parseInt(e.target.value || 5, 10))} className="h-11" data-testid="wf-setting-limit" /></div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Debounce (ms)</p><Input type="number" min="200" max="2000" step="50" value={f.debounce_ms ?? 400} onChange={(e) => set('debounce_ms', parseInt(e.target.value || 400, 10))} className="h-11" data-testid="wf-setting-debounce" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {WF_TEXT_FIELDS.map(([key, label]) => (
              <div key={key} className={['helper_text', 'no_match_message', 'disclaimer', 'fallback_message'].includes(key) ? 'sm:col-span-2' : ''}>
                <p className="text-xs font-bold text-slate-600 mb-1">{label}</p>
                {['helper_text', 'no_match_message', 'disclaimer', 'fallback_message', 'privacy_note'].includes(key) ? (
                  <Textarea value={f[key] || ''} onChange={(e) => set(key, e.target.value)} rows={2} data-testid={`wf-setting-${key}`} />
                ) : (
                  <Input value={f[key] || ''} onChange={(e) => set(key, e.target.value)} className="h-11" data-testid={`wf-setting-${key}`} />
                )}
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="wf-setting-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Ward Finder settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const WardsTab = () => {
  const [wards, setWards] = useState([]);
  const [busy, setBusy] = useState('');
  const [wrSettings, setWrSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/wards').then((r) => setWards(r.data || [])).catch(() => toast.error('Failed to load wards'));
    api.get('/admin/settings').then((r) => setWrSettings(r.data?.whos_running_map || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveWrSettings = async (patch) => {
    const next = { ...wrSettings, ...patch };
    setWrSettings(next);
    setSavingSettings(true);
    try {
      await api.put('/settings', { key: 'whos_running_map', value: next });
      toast.success("Who's Running display settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingSettings(false); }
  };

  const update = async (n, patch) => {
    try {
      await api.put(`/admin/wards/${n}`, patch);
      toast.success(`Ward ${n} updated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const uploadBoundary = async (n, file) => {
    if (!file) return;
    setBusy(`upload-${n}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/admin/wards/${n}/boundary`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data?.message || 'Boundary uploaded');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600 bg-white border border-slate-200 rounded-lg p-3">
        Official 8-ward boundaries are pre-loaded from the City of Kawartha Lakes open data (Wards 2018 layer). You can adjust each ward's overlay colour, opacity, and border, or upload a replacement boundary file (GeoJSON updates the live overlay; KML/SVG/PDF/image files are stored as references).
        {' '}<a href="https://www.kawarthalakes.ca/government-administration/elections/ward-boundaries/" target="_blank" rel="noreferrer" className="underline font-semibold">Official source</a>
      </p>
      <Card className="border-slate-200" data-testid="whos-running-map-settings">
        <CardContent className="p-4">
          <p className="font-serif font-bold text-[#1a2744] mb-1">Who's Running page — ward display settings</p>
          <p className="text-xs text-slate-600 mb-3">Controls the "Find Your Ward" map, the Browse by Ward cards, and the Ward 8 highlight on the public Who's Running page. Ward colours, opacity, borders, descriptions, source links and publish switches below are shared with the ward map system. {savingSettings && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Show "Find Your Ward" map section
              <Switch checked={wrSettings.show_find_your_ward !== false} onCheckedChange={(v) => saveWrSettings({ show_find_your_ward: v })} data-testid="wr-setting-find-your-ward" />
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Show Browse by Ward cards
              <Switch checked={wrSettings.show_ward_cards !== false} onCheckedChange={(v) => saveWrSettings({ show_ward_cards: v })} data-testid="wr-setting-ward-cards" />
            </label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Feature Ward 8 (Steve's race)
              <Switch checked={wrSettings.feature_ward8 !== false} onCheckedChange={(v) => saveWrSettings({ feature_ward8: v })} data-testid="wr-setting-feature-ward8" />
            </label>
          </div>
        </CardContent>
      </Card>
      <WardFinderSettings />
      <WardFinderMissPatterns />
      <WardFinderFeedback />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {wards.map((w) => (
          <Card key={w.ward_number} data-testid={`ward-admin-card-${w.ward_number}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><MapPinned className="h-4 w-4 text-[#C9A84C]" /> {w.label || `Ward ${w.ward_number}`}</p>
                <div className="flex items-center gap-2">
                  {w.has_geometry ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Boundary loaded</Badge> : <Badge className="bg-amber-100 text-amber-800 border-amber-200">No boundary</Badge>}
                  <Switch checked={w.published !== false} onCheckedChange={(v) => update(w.ward_number, { published: v })} data-testid={`ward-publish-${w.ward_number}`} />
                </div>
              </div>
              <WardForm ward={w} onSave={(patch) => update(w.ward_number, patch)} />
              <label className="mt-3 inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-[#EAF2FF] border border-[#CFE0FF] text-[#1a2744] text-sm font-bold cursor-pointer hover:bg-[#dcebff]">
                {busy === `upload-${w.ward_number}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload boundary (GeoJSON / KML / SVG / image / PDF)
                <input type="file" accept=".geojson,.json,.kml,.svg,.png,.jpg,.jpeg,.pdf" className="hidden" onChange={(e) => { uploadBoundary(w.ward_number, e.target.files?.[0]); e.target.value = ''; }} data-testid={`ward-boundary-upload-${w.ward_number}`} />
              </label>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const WardForm = ({ ward, onSave }) => {
  const [f, setF] = useState({
    label: ward.label || '', name: ward.name || '', description: ward.description || '',
    overlay_color: ward.overlay_color || '#1a2744', overlay_opacity: ward.overlay_opacity ?? 0.18,
    border_color: ward.border_color || '#1a2744', border_weight: ward.border_weight ?? 2,
    source_url: ward.source_url || '',
    card_summary: ward.card_summary || '',
    boundary_pdf_url: ward.boundary_pdf_url || '',
    sort_order: ward.sort_order ?? ward.ward_number,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <Input value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Label" className="h-10" />
        <Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Name" className="h-10" />
      </div>
      <Textarea value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Boundary description" rows={2} />
      <Textarea value={f.card_summary} onChange={(e) => set('card_summary', e.target.value)} placeholder="Short ward-card summary for the Who's Running page (optional — falls back to boundary description)" rows={2} data-testid={`ward-card-summary-${ward.ward_number}`} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
        <label className="text-xs font-semibold text-slate-600 flex flex-col gap-1">Fill
          <input type="color" value={f.overlay_color} onChange={(e) => set('overlay_color', e.target.value)} className="h-9 w-full rounded cursor-pointer border border-slate-200" data-testid={`ward-fill-${ward.ward_number}`} />
        </label>
        <label className="text-xs font-semibold text-slate-600 flex flex-col gap-1">Opacity ({Number(f.overlay_opacity).toFixed(2)})
          <input type="range" min="0" max="1" step="0.05" value={f.overlay_opacity} onChange={(e) => set('overlay_opacity', parseFloat(e.target.value))} data-testid={`ward-opacity-${ward.ward_number}`} />
        </label>
        <label className="text-xs font-semibold text-slate-600 flex flex-col gap-1">Border
          <input type="color" value={f.border_color} onChange={(e) => set('border_color', e.target.value)} className="h-9 w-full rounded cursor-pointer border border-slate-200" />
        </label>
        <label className="text-xs font-semibold text-slate-600 flex flex-col gap-1">Thickness ({f.border_weight})
          <input type="range" min="0.5" max="8" step="0.5" value={f.border_weight} onChange={(e) => set('border_weight', parseFloat(e.target.value))} data-testid={`ward-border-weight-${ward.ward_number}`} />
        </label>
      </div>
      <Input value={f.source_url} onChange={(e) => set('source_url', e.target.value)} placeholder="Source URL" className="h-10" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input value={f.boundary_pdf_url} onChange={(e) => set('boundary_pdf_url', e.target.value)} placeholder="Official ward boundary PDF URL" className="h-10" data-testid={`ward-pdf-url-${ward.ward_number}`} />
        <Input type="number" value={f.sort_order} onChange={(e) => set('sort_order', parseInt(e.target.value || ward.ward_number, 10))} placeholder="Sort order" className="h-10" data-testid={`ward-sort-order-${ward.ward_number}`} />
      </div>
      <Button size="sm" onClick={() => onSave(f)} className="h-10 bg-[#1a2744] text-white font-bold btn-press" data-testid={`ward-save-${ward.ward_number}`}>Save Ward {ward.ward_number}</Button>
    </div>
  );
};

// ----------------------------------------------------- Cost comparison tab ----
const CostTab = () => {
  const [big, setBig] = useState(null);
  const [cost, setCost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/admin/settings').then((r) => {
      setBig(r.data?.hub_big_picture || {});
      setCost(r.data?.cost_comparison || {});
    }).catch(() => { setBig({}); setCost({}); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', { key: 'hub_big_picture', value: big });
      await api.put('/settings', { key: 'cost_comparison', value: cost });
      toast.success('Homepage map content saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!big || !cost) return <p className="text-sm text-slate-500">Loading…</p>;
  const sb = (k, v) => setBig({ ...big, [k]: v });
  const sc = (k, v) => setCost({ ...cost, [k]: v });

  return (
    <div className="space-y-6 max-w-3xl">
      <Card><CardContent className="p-5 space-y-3">
        <p className="font-serif font-bold text-[#1a2744]">"See the Big Picture" homepage section</p>
        <Input value={big.title || ''} onChange={(e) => sb('title', e.target.value)} placeholder="Title" className="h-11" />
        <Input value={big.subtitle || ''} onChange={(e) => sb('subtitle', e.target.value)} placeholder="Subtitle" className="h-11" />
        <Textarea value={big.text || ''} onChange={(e) => sb('text', e.target.value)} rows={3} placeholder="Main text" />
        <Textarea value={big.campaign_message || ''} onChange={(e) => sb('campaign_message', e.target.value)} rows={3} placeholder="Campaign message" />
        <Input value={big.copy_title || ''} onChange={(e) => sb('copy_title', e.target.value)} placeholder="Copy block title" className="h-11" />
        <Textarea value={big.copy_text || ''} onChange={(e) => sb('copy_text', e.target.value)} rows={4} placeholder="Copy block text" />
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Published <Switch checked={big.published !== false} onCheckedChange={(v) => sb('published', v)} /></label>
      </CardContent></Card>

      <Card className="border-amber-300"><CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-serif font-bold text-[#1a2744]">City System Cost Comparison</p>
          <Badge className={cost.status === 'verified' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'} data-testid="cost-admin-status-badge">
            {cost.status === 'verified' ? 'Verified' : 'Needs Review'}
          </Badge>
        </div>
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="cost-admin-warning">
          <strong>Do not publish a dollar amount unless you have a verified source.</strong> The $800,000/year claim (or any figure) only appears publicly when: status is <strong>Verified</strong>, a source name is filled in, a source URL or source document is attached, AND the block is published. Until then only the softer, review-safe wording is shown.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input value={cost.claim_title || ''} onChange={(e) => sc('claim_title', e.target.value)} placeholder="Claim title (e.g. City System Cost Claim)" className="h-11" data-testid="cost-admin-claim-title" />
          <Input value={cost.year || ''} onChange={(e) => sc('year', e.target.value)} placeholder="Year (e.g. 2025)" className="h-11" data-testid="cost-admin-year" />
        </div>
        <Input value={cost.public_title || ''} onChange={(e) => sc('public_title', e.target.value)} placeholder="Public title" className="h-11" />
        <Textarea value={cost.public_text || ''} onChange={(e) => sc('public_text', e.target.value)} rows={4} placeholder="Public text (review-safe, no dollar figures)" data-testid="cost-admin-public-text" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input value={cost.annual_cost || ''} onChange={(e) => sc('annual_cost', e.target.value)} placeholder='Annual cost (e.g. "$800,000/year") — only shows when Verified' className="h-11" data-testid="cost-admin-amount" />
          <Select value={cost.status || 'needs_review'} onValueChange={(v) => sc('status', v)}>
            <SelectTrigger className="h-11" data-testid="cost-admin-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="needs_review">Needs Review (hide figures)</SelectItem>
              <SelectItem value="verified">Verified (source required)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input value={cost.source_name || ''} onChange={(e) => sc('source_name', e.target.value)} placeholder="Source name" className="h-11" />
          <Input value={cost.source_url || ''} onChange={(e) => sc('source_url', e.target.value)} placeholder="Official source document / link URL" className="h-11" data-testid="cost-admin-source-url" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg bg-[#EAF2FF] border border-[#CFE0FF] text-[#1a2744] text-sm font-bold cursor-pointer hover:bg-[#dcebff]">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload source document (PDF / image)
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" className="hidden" data-testid="cost-admin-doc-upload"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                setUploading(true);
                try {
                  const up = await uploadFile(file, 'source_documents');
                  sc('source_doc_url', up.url);
                  toast.success('Source document attached — remember to Save');
                } catch { toast.error('Upload failed'); }
                finally { setUploading(false); }
              }} />
          </label>
          {cost.source_doc_url && (
            <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-2" data-testid="cost-admin-doc-attached">
              Document attached · <a href={cost.source_doc_url.startsWith('http') ? cost.source_doc_url : `${process.env.REACT_APP_BACKEND_URL}${cost.source_doc_url}`} target="_blank" rel="noreferrer" className="underline">view</a>
              <button onClick={() => sc('source_doc_url', '')} className="text-red-500 underline">remove</button>
            </span>
          )}
        </div>
        <Textarea value={cost.explanation || ''} onChange={(e) => sc('explanation', e.target.value)} rows={2} placeholder="Explanation (optional)" />
        <Textarea value={cost.steve_statement || ''} onChange={(e) => sc('steve_statement', e.target.value)} rows={2} placeholder="Steve's statement (optional, shown as Steve's Comment)" />
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Published <Switch checked={cost.published !== false} onCheckedChange={(v) => sc('published', v)} data-testid="cost-admin-published" /></label>
      </CardContent></Card>

      <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="cost-admin-save">
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save homepage map content
      </Button>
    </div>
  );
};

// ---------------------------------------------- Who's Running: candidates ----
const OFFICES = [
  'Mayor',
  'Councillor - Ward 1', 'Councillor - Ward 2', 'Councillor - Ward 3', 'Councillor - Ward 4',
  'Councillor - Ward 5', 'Councillor - Ward 6', 'Councillor - Ward 7', 'Councillor - Ward 8',
  'English-Public School Board Trustee (TLDSB) - Wards 1 & 4',
  'English-Public School Board Trustee (TLDSB) - Wards 2 & 3',
  'English-Public School Board Trustee (TLDSB) - Ward 5',
  'English-Public School Board Trustee (TLDSB) - Wards 6 & 8',
  'English-Public School Board Trustee (TLDSB) - Ward 7',
  'English-Catholic School Board Trustee (PVNC)',
  'French-Public School Board Trustee (Viamonde)',
  'French-Catholic School Board Trustee (MonAvenir)',
  'Third Party Advertiser',
];
const CANDIDATE_STATUSES = ['unofficial', 'certified', 'withdrawn', 'rejected', 'needs_review', 'hidden'];
const CANDIDATE_REVIEW_STATUSES = ['sample', 'needs_review', 'source_based', 'verified', 'published', 'hidden'];
const CANDIDATE_TYPES = [
  { value: 'candidate', label: 'Candidate' },
  { value: 'school_board_trustee', label: 'School Board Trustee' },
  { value: 'third_party_advertiser', label: 'Third Party Advertiser' },
  { value: 'campaign', label: 'Campaign card (Steve)' },
];
const UPDATE_TYPES = [
  { value: 'candidate_filed', label: 'New candidate filed' },
  { value: 'candidate_withdrawn', label: 'Candidate withdrawn' },
  { value: 'candidate_certified', label: 'Candidate certified' },
  { value: 'website_added', label: 'Candidate website added' },
  { value: 'debate_announced', label: 'Debate announced' },
  { value: 'forum_announced', label: 'Candidate forum announced' },
  { value: 'ckl_notice', label: 'CKL election notice posted' },
  { value: 'steves_statement', label: "Steve's election statement" },
  { value: 'other', label: 'Other' },
];
const UPDATE_SOURCE_TYPES = [
  { value: 'official', label: 'Official Source' },
  { value: 'local_news', label: 'Local News' },
  { value: 'steves_comment', label: "Steve's Comment" },
];

const STATUS_BADGE_CLS = {
  unofficial: 'bg-slate-100 text-slate-700 border-slate-300',
  certified: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  withdrawn: 'bg-red-50 text-red-700 border-red-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  needs_review: 'bg-amber-100 text-amber-800 border-amber-200',
  hidden: 'bg-slate-200 text-slate-500 border-slate-300',
};
const REVIEW_BADGE_CLS = {
  source_based: 'bg-sky-50 text-sky-800 border-sky-200',
  needs_review: 'bg-amber-100 text-amber-800 border-amber-200',
  verified: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  sample: 'bg-amber-100 text-amber-800 border-amber-200',
  published: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  hidden: 'bg-slate-200 text-slate-500 border-slate-300',
};

const EMPTY_CANDIDATE = {
  first_name: '', last_name: '', display_name: '', office: 'Mayor', ward: '',
  candidate_type: 'candidate', status: 'needs_review', review_status: 'needs_review',
  published: false, show_private_contact: false, is_campaign: false, manual_override: false,
  website_url: '', social_url: '', public_notes: '', admin_notes: '',
  private_email: '', private_phone: '', public_email: '', public_phone: '',
  source_name: 'City of Kawartha Lakes Candidates List',
  source_url: 'https://www.kawarthalakes.ca/government-administration/elections/candidates-list/',
  source_checked_date: '', filing_date: '', withdrawn_date: '',
};

const CandidateDialog = ({ open, onOpenChange, candidate, onSaved }) => {
  const [f, setF] = useState(EMPTY_CANDIDATE);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF({ ...EMPTY_CANDIDATE, ...(candidate || {}) }); }, [candidate, open]);
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!f.first_name && !f.last_name && !f.display_name) { toast.error('A candidate name is required'); return; }
    setSaving(true);
    const payload = { ...f, ward: f.ward === '' || f.ward === 'none' ? null : parseInt(f.ward, 10), display_name: f.display_name || `${f.first_name} ${f.last_name}`.trim() };
    delete payload._id; delete payload.created_at; delete payload.created_by; delete payload.updated_at; delete payload.updated_by;
    try {
      if (candidate?.id) await api.put(`/admin/candidates/${candidate.id}`, payload);
      else await api.post('/admin/candidates', payload);
      toast.success('Candidate saved');
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-[#1a2744]">{candidate?.id ? `Edit: ${candidate.display_name}` : 'Add candidate'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input value={f.first_name} onChange={(e) => set('first_name', e.target.value)} placeholder="First name" className="h-11" data-testid="candidate-form-first-name" />
            <Input value={f.last_name} onChange={(e) => set('last_name', e.target.value)} placeholder="Last name" className="h-11" data-testid="candidate-form-last-name" />
            <Input value={f.display_name} onChange={(e) => set('display_name', e.target.value)} placeholder="Display name (optional)" className="h-11" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-xs font-bold text-slate-600 mb-1">Office</p>
              <Select value={f.office} onValueChange={(v) => set('office', v)}>
                <SelectTrigger className="h-11" data-testid="candidate-form-office"><SelectValue /></SelectTrigger>
                <SelectContent>{OFFICES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Ward (Councillor races)</p>
              <Select value={f.ward === null || f.ward === '' ? 'none' : String(f.ward)} onValueChange={(v) => set('ward', v === 'none' ? '' : v)}>
                <SelectTrigger className="h-11" data-testid="candidate-form-ward"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">No ward</SelectItem>{[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>Ward {n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><p className="text-xs font-bold text-slate-600 mb-1">Type</p>
              <Select value={f.candidate_type} onValueChange={(v) => set('candidate_type', v)}>
                <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>{CANDIDATE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Status</p>
              <Select value={f.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger className="h-11" data-testid="candidate-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CANDIDATE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Review status</p>
              <Select value={f.review_status} onValueChange={(v) => set('review_status', v)}>
                <SelectTrigger className="h-11" data-testid="candidate-form-review-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CANDIDATE_REVIEW_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input value={f.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="Candidate website URL" className="h-11" data-testid="candidate-form-website" />
            <Input value={f.social_url} onChange={(e) => set('social_url', e.target.value)} placeholder="Candidate social link" className="h-11" />
          </div>
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
            <p className="text-xs font-bold text-sky-900 mb-2">Contact details published by the City on the official CKL list (shown publicly)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={f.public_email || ''} onChange={(e) => set('public_email', e.target.value)} placeholder="Email published by CKL" className="h-11 bg-white" data-testid="candidate-form-public-email" />
              <Input value={f.public_phone || ''} onChange={(e) => set('public_phone', e.target.value)} placeholder="Telephone published by CKL" className="h-11 bg-white" data-testid="candidate-form-public-phone" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input value={f.source_name} onChange={(e) => set('source_name', e.target.value)} placeholder="Source name" className="h-11" />
            <Input value={f.source_url} onChange={(e) => set('source_url', e.target.value)} placeholder="Source URL" className="h-11" data-testid="candidate-form-source-url" />
            <div><p className="text-xs font-bold text-slate-600 mb-1">Source checked date</p><Input type="date" value={f.source_checked_date || ''} onChange={(e) => set('source_checked_date', e.target.value)} className="h-11" data-testid="candidate-form-source-checked" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><p className="text-xs font-bold text-slate-600 mb-1">Filing date (optional)</p><Input type="date" value={f.filing_date || ''} onChange={(e) => set('filing_date', e.target.value)} className="h-11" /></div>
            <div><p className="text-xs font-bold text-slate-600 mb-1">Withdrawn date (if withdrawn)</p><Input type="date" value={f.withdrawn_date || ''} onChange={(e) => set('withdrawn_date', e.target.value)} className="h-11" /></div>
          </div>
          <Textarea value={f.public_notes} onChange={(e) => set('public_notes', e.target.value)} rows={2} placeholder="Public note (shown on the candidate card)" data-testid="candidate-form-public-notes" />
          <Textarea value={f.admin_notes} onChange={(e) => set('admin_notes', e.target.value)} rows={2} placeholder="Admin note (never shown publicly)" data-testid="candidate-form-admin-notes" />
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <Switch checked={!!f.show_private_contact} onCheckedChange={(v) => set('show_private_contact', v)} data-testid="candidate-form-show-private" />
              Show private contact info publicly (off by default — keep off unless intentional)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input value={f.private_email} onChange={(e) => set('private_email', e.target.value)} placeholder="Private email (hidden unless enabled)" className="h-11 bg-white" data-testid="candidate-form-private-email" />
              <Input value={f.private_phone} onChange={(e) => set('private_phone', e.target.value)} placeholder="Private phone (hidden unless enabled)" className="h-11 bg-white" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Published <Switch checked={!!f.published} onCheckedChange={(v) => set('published', v)} data-testid="candidate-form-published" /></label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Ward 8 campaign card (Steve) <Switch checked={!!f.is_campaign} onCheckedChange={(v) => set('is_campaign', v)} /></label>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">Manual override (lock from daily refresh) <Switch checked={!!f.manual_override} onCheckedChange={(v) => set('manual_override', v)} data-testid="candidate-form-manual-override" /></label>
          </div>
          <p className="text-[11px] text-slate-500">Editing name, office, ward, status, dates, web link, or CKL-published contact details automatically locks this record with a Manual override so the daily official-source refresh will not overwrite your change. Turn the override off to let the refresh manage this record again.</p>
          <Button onClick={save} disabled={saving} className="h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full sm:w-auto" data-testid="candidate-form-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save candidate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---- Official Source Refresh panel (Who's Running) ----
const CKL_SOURCE_URL = 'https://www.kawarthalakes.ca/government-administration/elections/candidates-list/';
const fmtEastern = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'medium', timeStyle: 'short' }) + ' ET';
  } catch { return iso; }
};

const CHANGE_TYPE_BADGES = {
  added: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  removed: 'bg-red-50 text-red-700 border-red-200',
  withdrawn: 'bg-red-50 text-red-700 border-red-200',
  restored: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  field_changed: 'bg-sky-50 text-sky-800 border-sky-200',
  manual_override_skipped: 'bg-amber-100 text-amber-800 border-amber-200',
};

const RefreshLogRow = ({ log }) => {
  const [open, setOpen] = useState(false);
  const changes = log.changes || [];
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`refresh-log-${log.id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex flex-wrap items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50" data-testid={`refresh-log-toggle-${log.id}`}>
        <Badge variant="outline" className={`text-xs font-bold ${log.success ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
          {log.success ? 'Success' : 'Failed'}
        </Badge>
        <span className="text-xs font-bold text-[#1a2744]">{fmtEastern(log.run_at)}</span>
        <span className="text-[11px] text-slate-500 capitalize">{log.trigger} · by {log.run_by}</span>
        <span className="text-[11px] text-slate-500 ml-auto">
          {log.success ? `${log.official_count} on official list · ${changes.length} change record(s)` : 'Cached data preserved'}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5 bg-slate-50/60">
          {!log.success && <p className="text-xs text-red-700 font-semibold pt-2">{log.error}</p>}
          {log.alert_email_to && (
            <p className="text-xs pt-1 text-slate-600" data-testid={`refresh-log-alert-${log.id}`}>
              Alert email {log.alert_email_sent ? 'sent' : `FAILED (${log.alert_email_error || 'unknown error'})`} to {log.alert_email_to}
            </p>
          )}
          {(log.parse_warnings || []).map((w, i) => (
            <p key={`${log.id}-warn-${i}-${String(w).slice(0, 24)}`} className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">{w}</p>
          ))}
          {log.success && changes.length === 0 && <p className="text-xs text-slate-500 pt-2">No differences found — stored data already matches the official list.</p>}
          {changes.map((c, i) => (
            <div key={`${log.id}-chg-${i}-${c.name || ''}-${c.field || ''}`} className="text-xs bg-white border border-slate-200 rounded p-2" data-testid={`refresh-change-${log.id}-${i}`}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className={`text-[10px] font-bold ${CHANGE_TYPE_BADGES[c.type] || 'border-slate-300 text-slate-600'}`}>{(c.type || '').replace(/_/g, ' ')}</Badge>
                <span className="font-bold text-[#1a2744]">{c.name}</span>
                <span className="text-slate-500">{c.office}</span>
                {c.field && <span className="text-slate-500">· {c.field}</span>}
              </div>
              <p className="mt-1 text-slate-600">
                <span className="font-semibold">Before:</span> {String(c.before ?? '—') || '—'} <span className="mx-1 text-slate-400">→</span> <span className="font-semibold">After:</span> {String(c.after ?? '—') || '—'}
              </p>
              {c.note && <p className="mt-0.5 text-amber-800">{c.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SourceRefreshPanel = ({ onRefreshed }) => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/candidates/refresh-status').then((r) => setStatus(r.data)).catch(() => {});
    api.get('/admin/candidates/refresh-logs').then((r) => setLogs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/admin/candidates/refresh');
      const d = r.data || {};
      if (d.success) {
        const applied = (d.changes || []).filter((c) => c.type !== 'manual_override_skipped').length;
        toast.success(`Refresh complete — ${d.official_count} candidates on the official CKL list, ${applied} change(s) applied.`);
      } else {
        toast.error(`Refresh failed — cached data preserved. ${d.error || ''}`);
      }
      load();
      onRefreshed?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const toggleAuto = async (v) => {
    try {
      const r = await api.put('/admin/candidates/refresh-settings', { auto_refresh_enabled: v });
      setStatus((prev) => ({ ...(prev || {}), ...r.data }));
      toast.success(v ? 'Automatic daily refresh turned ON (7:00 PM Eastern)' : 'Automatic daily refresh turned OFF');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the setting'); }
  };

  const toggleEmailAlerts = async (v) => {
    try {
      const r = await api.put('/admin/candidates/refresh-settings', { email_alerts_enabled: v });
      setStatus((prev) => ({ ...(prev || {}), ...r.data }));
      toast.success(v ? 'Email alerts turned ON — Steve gets an email when the nightly refresh fails or finds changes' : 'Email alerts turned OFF');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the setting'); }
  };

  const sendTestAlert = async () => {
    setTestingAlert(true);
    try {
      const r = await api.post('/admin/candidates/refresh-alert-test');
      toast.success(`Test alert sent to ${r.data.to} — check the inbox.`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test alert could not be sent'); }
    finally { setTestingAlert(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="whos-running-refresh-panel"><CardContent className="p-4 sm:p-5">
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 justify-between">
        <div className="min-w-0">
          <p className="font-serif font-bold text-[#1a2744]">Official Source Refresh — Who's Running</p>
          <p className="text-xs text-slate-600 mt-1 max-w-2xl">
            The public Who's Running page is kept in sync with the <strong>official City of Kawartha Lakes Candidates List</strong> — the only source used.
            Additions, withdrawals, removals, and contact/link changes are applied automatically and every change is logged with before/after details.
            If the City's page cannot be reached, the cached public data is preserved and you are alerted here.
          </p>
          <p className="text-xs text-slate-600 mt-1.5">
            Source URL:{' '}
            <a href={CKL_SOURCE_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold break-all" data-testid="refresh-panel-source-url">{CKL_SOURCE_URL}</a>
          </p>
        </div>
        <div className="flex flex-col gap-2.5 shrink-0">
          <Button onClick={runNow} disabled={refreshing} className="h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="run-refresh-now-btn">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />} Run Refresh Now
          </Button>
          <label className="flex items-center justify-between gap-2 text-xs font-bold text-slate-700">
            Auto-refresh daily at 7:00 PM Eastern
            <Switch checked={status?.auto_refresh_enabled !== false} onCheckedChange={toggleAuto} data-testid="auto-refresh-toggle" />
          </label>
          <label className="flex items-center justify-between gap-2 text-xs font-bold text-slate-700">
            Email Steve on failure or changes
            <Switch checked={status?.email_alerts_enabled !== false} onCheckedChange={toggleEmailAlerts} data-testid="email-alerts-toggle" />
          </label>
          {status?.email_alerts_enabled !== false && (
            <div className="text-right">
              <p className="text-[11px] text-slate-500 break-all" data-testid="alert-email-recipient">Alerts go to: <strong>{status?.alert_email || '…'}</strong></p>
              <Button size="sm" variant="outline" onClick={sendTestAlert} disabled={testingAlert} className="h-8 mt-1 text-[11px] font-bold border-slate-300 text-[#1a2744] btn-press" data-testid="send-test-alert-btn">
                {testingAlert ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null} Send test alert email
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-2.5" data-testid="refresh-status-grid">
        {[
          ['Last checked', fmtEastern(status?.last_checked)],
          ['Last updated', fmtEastern(status?.last_updated)],
          ['Last success', fmtEastern(status?.last_success)],
          ['Last failure', status?.last_failure ? fmtEastern(status.last_failure) : 'None recorded'],
          ['Next scheduled refresh', status?.auto_refresh_enabled === false ? 'Auto-refresh is off' : fmtEastern(status?.next_refresh)],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-xs font-bold text-[#1a2744] mt-0.5" data-testid={`refresh-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
          </div>
        ))}
      </div>
      {status?.last_error && (
        <p className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="refresh-last-error">Last failure: {status.last_error} (the public page kept the last good data)</p>
      )}
      <p className="mt-3 text-[11px] text-slate-500">
        Manual edits lock a record with a <strong>Manual override</strong> badge so the refresh never silently overwrites your change; skipped differences still appear in the log for review. Refresh logs are admin-only and never shown publicly.
      </p>

      <div className="mt-3">
        <Button variant="outline" onClick={() => setShowLogs((v) => !v)} className="h-10 text-xs font-bold border-slate-300 text-[#1a2744] btn-press" data-testid="refresh-logs-toggle">
          <CalendarClock className="h-3.5 w-3.5 mr-1.5" /> {showLogs ? 'Hide refresh logs' : `View refresh logs (${logs.length})`}
        </Button>
        {showLogs && (
          <div className="mt-2.5 space-y-2" data-testid="refresh-logs-list">
            {logs.length === 0 && <p className="text-xs text-slate-500">No refresh runs recorded yet.</p>}
            {logs.map((log) => <RefreshLogRow key={log.id} log={log} />)}
          </div>
        )}
      </div>
    </CardContent></Card>
  );
};

const CandidatesTab = () => {
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/candidates').then((r) => setCandidates(r.data || [])).catch(() => toast.error('Failed to load candidates'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (c, changes, msg) => {
    setBusy(c.id);
    try {
      await api.put(`/admin/candidates/${c.id}`, changes);
      toast.success(msg || 'Candidate updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
    finally { setBusy(''); }
  };

  const createUpdate = async (c, updateType) => {
    setBusy(c.id);
    try {
      await api.post(`/admin/candidates/${c.id}/create-update`, { update_type: updateType });
      toast.success('Draft update created in Candidate Updates (Needs Review, unpublished)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create update'); }
    finally { setBusy(''); }
  };

  const createSocial = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/admin/candidates/${c.id}/social-draft`);
      toast.success('Facebook/social draft created — review it in Admin → Social Media Drafts');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create social draft'); }
    finally { setBusy(''); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.display_name}? This cannot be undone. (You can Hide instead.)`)) return;
    try { await api.delete(`/admin/candidates/${c.id}`); toast.success('Candidate deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const filtered = candidates.filter((c) => !search || `${c.display_name} ${c.office} ${c.status}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <SourceRefreshPanel onRefreshed={load} />

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, office, or status…" className="h-11 sm:max-w-xs bg-white" data-testid="candidate-admin-search" />
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="candidate-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add candidate
        </Button>
      </div>

      <div className="space-y-2.5">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`candidate-admin-row-${c.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="font-bold text-[#1a2744]">{c.display_name}</p>
                    {c.is_campaign && <Badge className="bg-[#C9A84C] text-[#1a2744] border-0 text-xs font-bold">Campaign card</Badge>}
                    {c.manual_override && <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs font-bold" data-testid={`candidate-override-badge-${c.id}`}>Manual override</Badge>}
                    <Badge variant="outline" className={`${STATUS_BADGE_CLS[c.status] || STATUS_BADGE_CLS.unofficial} text-xs font-semibold`}>{(c.status || '').replace('_', ' ')}</Badge>
                    <Badge variant="outline" className={`${REVIEW_BADGE_CLS[c.review_status] || REVIEW_BADGE_CLS.needs_review} text-xs font-semibold`}>{(c.review_status || '').replace('_', ' ')}</Badge>
                    {c.show_private_contact && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs font-semibold">Private contact PUBLIC</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">{c.office}{c.source_checked_date ? ` · checked ${c.source_checked_date}` : ''}</p>
                  {c.admin_notes && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5 mt-1.5 whitespace-pre-line max-w-2xl">{c.admin_notes}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600">Public
                    <Switch checked={!!c.published} onCheckedChange={(v) => patch(c, { published: v }, v ? 'Published' : 'Unpublished')} data-testid={`candidate-publish-${c.id}`} />
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`candidate-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                {c.review_status !== 'source_based' && <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-sky-300 text-sky-800" onClick={() => patch(c, { review_status: 'source_based' }, 'Marked Source-Based')} data-testid={`candidate-source-based-${c.id}`}>Mark Source-Based</Button>}
                {c.review_status !== 'verified' && <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-emerald-300 text-emerald-800" onClick={() => patch(c, { review_status: 'verified' }, 'Marked Verified')} data-testid={`candidate-verify-${c.id}`}>Mark Verified</Button>}
                {c.status !== 'withdrawn' && <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-red-200 text-red-700" onClick={() => patch(c, { status: 'withdrawn', withdrawn_date: new Date().toISOString().slice(0, 10) }, 'Marked Withdrawn')} data-testid={`candidate-withdraw-${c.id}`}>Mark Withdrawn</Button>}
                {c.status !== 'hidden' ? (
                  <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300 text-slate-600" onClick={() => patch(c, { status: 'hidden', published: false }, 'Hidden from public view')} data-testid={`candidate-hide-${c.id}`}>Hide</Button>
                ) : (
                  <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300 text-slate-600" onClick={() => patch(c, { status: 'needs_review' }, 'Unhidden — set to Needs Review')}>Unhide</Button>
                )}
                <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300" disabled={busy === c.id} onClick={() => createUpdate(c, c.status === 'withdrawn' ? 'candidate_withdrawn' : 'candidate_filed')} data-testid={`candidate-create-update-${c.id}`}><Megaphone className="h-3.5 w-3.5 mr-1" /> Create update</Button>
                <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-slate-300" disabled={busy === c.id} onClick={() => createSocial(c)} data-testid={`candidate-social-draft-${c.id}`}><Share2 className="h-3.5 w-3.5 mr-1" /> Social draft</Button>
                {c.manual_override && (
                  <Button size="sm" variant="outline" className="h-9 text-xs font-bold border-amber-300 text-amber-800" onClick={() => patch(c, { manual_override: false }, 'Manual override cleared — the daily refresh will manage this record again')} data-testid={`candidate-clear-override-${c.id}`}>Clear override</Button>
                )}
                <Button size="sm" variant="ghost" className="h-9 text-xs font-bold text-red-500" onClick={() => remove(c)} data-testid={`candidate-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">No candidates match your search.</p>}
      </div>

      <CandidateDialog open={dialogOpen} onOpenChange={setDialogOpen} candidate={editing} onSaved={load} />
    </div>
  );
};

// ------------------------------------------------------------------ page ----
export default function AdminElection() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'content');
  return (
    <div>
      <h1 className="font-serif font-black text-2xl text-[#1a2744]">2026 Election Centre</h1>
      <p className="text-sm text-slate-500 mb-4">Neutral, source-based election hub. All news imports are <strong>draft-first</strong>; nothing is public until an item is set to Published. Sources are managed in Settings → Source Monitoring.</p>
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5">
        {[['content', 'Centre Content'], ['key-dates', 'Key Dates'], ['candidates', "Who's Running"], ['candidate-updates', 'Candidate Updates'], ['news', 'Election News'], ['debates', 'Debate Watch'], ['wards', 'Wards & Map'], ['cost', 'Homepage Map & Cost Block']].map(([v, l]) => (
          <Button key={v} onClick={() => setTab(v)} variant={tab === v ? 'default' : 'outline'} className={`h-11 shrink-0 font-bold btn-press ${tab === v ? 'bg-[#1a2744] text-white' : 'border-slate-300 text-[#1a2744]'}`} data-testid={`election-admin-tab-${v}`}>{l}</Button>
        ))}
      </div>

      {tab === 'content' && <ContentTab />}
      {tab === 'key-dates' && <KeyDatesTab />}
      {tab === 'candidates' && <CandidatesTab />}
      {tab === 'wards' && <WardsTab />}
      {tab === 'cost' && <CostTab />}

      {tab === 'candidate-updates' && (
        <AdminCrud
          title="Candidate updates & election notices"
          description="Draft-first: updates only appear on the public Who's Running page when Published. Keep Official Source, Local News, and Steve's Comment items separate via the source type."
          listEndpoint="/admin/candidate-updates"
          createEndpoint="/admin/candidate-updates"
          updateEndpoint={(i) => `/admin/candidate-updates/${i.id}`}
          deleteEndpoint={(i) => `/admin/candidate-updates/${i.id}`}
          searchKeys={['title', 'candidate_name', 'office', 'source_name']}
          columns={[
            { key: 'title', label: 'Title' },
            { key: 'update_type', label: 'Type', render: (i) => (UPDATE_TYPES.find((t) => t.value === i.update_type)?.label || i.update_type) },
            { key: 'source_type', label: 'Source', render: (i) => (UPDATE_SOURCE_TYPES.find((t) => t.value === i.source_type)?.label || i.source_type) },
            { key: 'published', label: 'Public', render: (i) => (i.published ? 'Yes' : 'No') },
            { key: 'date', label: 'Date' },
          ]}
          fields={[
            { name: 'title', label: 'Title' },
            { name: 'summary', label: 'Plain-language summary', type: 'textarea' },
            { name: 'date', label: 'Date', type: 'date' },
            { name: 'update_type', label: 'Update type', type: 'select', default: 'other', options: UPDATE_TYPES },
            { name: 'source_type', label: 'Source type', type: 'select', default: 'official', options: UPDATE_SOURCE_TYPES },
            { name: 'source_name', label: 'Source name' },
            { name: 'source_url', label: 'Source URL' },
            { name: 'office', label: 'Office (optional)' },
            { name: 'ward', label: 'Ward (1-8, optional)' },
            { name: 'candidate_name', label: 'Candidate name (if relevant)' },
            { name: 'review_status', label: 'Review status', type: 'select', default: 'needs_review', options: CANDIDATE_REVIEW_STATUSES.map((v) => ({ value: v, label: v.replace('_', ' ') })) },
            { name: 'published', label: 'Published', type: 'switch', default: false },
          ]}
        />
      )}

      {tab === 'news' && (
        <AdminCrud
          title="Election news & candidate updates"
          description="Draft-first: items are only public when status is set to Published. Keep official facts separate from Steve's commentary."
          listEndpoint="/admin/election-items"
          createEndpoint="/admin/election-items"
          updateEndpoint={(i) => `/admin/election-items/${i.id}`}
          deleteEndpoint={(i) => `/admin/election-items/${i.id}`}
          searchKeys={['title', 'source_name', 'candidate_name']}
          columns={[
            { key: 'title', label: 'Title' },
            { key: 'category', label: 'Category', render: (i) => (ELECTION_CATEGORIES.find((c) => c.value === i.category)?.label || i.category) },
            { key: 'status', label: 'Status', render: (i) => <Badge className={i.status === 'published' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}>{i.status}</Badge> },
            { key: 'date', label: 'Date' },
          ]}
          fields={[
            { name: 'title', label: 'Title' },
            { name: 'summary', label: 'Plain-language summary', type: 'textarea' },
            { name: 'body', label: 'Body (optional)', type: 'textarea' },
            { name: 'why_it_matters', label: 'Why it matters', type: 'textarea' },
            { name: 'category', label: 'Category', type: 'select', default: 'other', options: ELECTION_CATEGORIES },
            { name: 'status', label: 'Status (draft-first)', type: 'select', default: 'draft', options: ITEM_STATUSES.map((v) => ({ value: v, label: v.replace('_', ' ') })) },
            { name: 'date', label: 'Date', type: 'date' },
            { name: 'source_name', label: 'Source name' },
            { name: 'source_url', label: 'Source URL' },
            { name: 'ward', label: 'Ward (1-8, optional)' },
            { name: 'candidate_name', label: 'Candidate name (if relevant)' },
            { name: 'steve_comment', label: "Steve's commentary (kept separate from source facts)", type: 'textarea' },
          ]}
        />
      )}

      {tab === 'debates' && (
        <AdminCrud
          title="Debates, forums & town halls"
          description="Never public unless Published AND (a source link exists OR status is Confirmed/Completed)."
          listEndpoint="/admin/debates"
          createEndpoint="/admin/debates"
          updateEndpoint={(i) => `/admin/debates/${i.id}`}
          deleteEndpoint={(i) => `/admin/debates/${i.id}`}
          searchKeys={['title', 'organizer']}
          columns={[
            { key: 'title', label: 'Title' },
            { key: 'date', label: 'Date' },
            { key: 'status', label: 'Status', render: (i) => <Badge className={['confirmed', 'completed'].includes(i.status) ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}>{i.status}</Badge> },
            { key: 'published', label: 'Public', render: (i) => (i.published ? 'Yes' : 'No') },
          ]}
          fields={[
            { name: 'title', label: 'Title' },
            { name: 'organizer', label: 'Organizer' },
            { name: 'date', label: 'Date', type: 'date' },
            { name: 'time', label: 'Time (e.g. 7:00 PM)' },
            { name: 'location', label: 'Location' },
            { name: 'livestream_url', label: 'Livestream link' },
            { name: 'registration_url', label: 'Registration link' },
            { name: 'source_name', label: 'Source name' },
            { name: 'source_url', label: 'Source link (required to publish unless Confirmed)' },
            { name: 'status', label: 'Status', type: 'select', default: 'needs_review', options: DEBATE_STATUSES.map((v) => ({ value: v, label: v.replace('_', ' ') })) },
            { name: 'published', label: 'Published', type: 'switch', default: false },
          ]}
        />
      )}
    </div>
  );
}
